import { type Address, zeroAddress } from "viem";

import rwaAddresses from "./addresses.rwa.json";
import rwaTestnetAddresses from "./addresses.rwaTestnet.json";
import { ARBITRUM, BOTANIX, ASSETCHAIN, ASSETCHAIN_TESTNET } from "./chains";
import { getEnv } from "utils/env";

export const CONTRACTS = {
  [ARBITRUM]: {
    Vault: zeroAddress,
    Router: zeroAddress,
    VaultReader: zeroAddress,
    Reader: rwaAddresses.Reader as Address,
    GlpManager: zeroAddress,
    RewardRouter: zeroAddress,
    RewardReader: zeroAddress,
    GlpRewardRouter: zeroAddress,
    NATIVE_TOKEN: zeroAddress,
    GLP: zeroAddress,
    GMX: zeroAddress,
    ES_GMX: zeroAddress,
    BN_GMX: zeroAddress,
    USDG: zeroAddress,
    ES_GMX_IOU: zeroAddress,

    StakedGmxTracker: zeroAddress,
    BonusGmxTracker: zeroAddress,
    FeeGmxTracker: zeroAddress,
    StakedGlpTracker: zeroAddress,
    FeeGlpTracker: zeroAddress,
    ExtendedGmxTracker: zeroAddress,

    StakedGmxDistributor: zeroAddress,
    StakedGlpDistributor: zeroAddress,

    GmxVester: zeroAddress,
    GlpVester: zeroAddress,
    AffiliateVester: zeroAddress,

    OrderBook: zeroAddress,
    OrderExecutor: zeroAddress,
    OrderBookReader: zeroAddress,

    PositionRouter: zeroAddress,
    PositionManager: zeroAddress,

    TraderJoeGmxAvaxPool: zeroAddress,
    ReferralStorage: rwaAddresses.ReferralStorage as Address,
    ReferralReader: zeroAddress,

    // Synthetics
    DataStore: rwaAddresses.DataStore as Address,
    EventEmitter: zeroAddress,
    ExchangeRouter: zeroAddress,
    SubaccountRouter: zeroAddress,
    DepositVault: zeroAddress,
    WithdrawalVault: zeroAddress,
    // GMX v2 OrderVault (used in multicall tests)
    OrderVault: "0x31ef83a530fde1b38ee9a18093a333d8bbbc40d5",
    ShiftVault: zeroAddress,
    SyntheticsReader: rwaAddresses.Reader as Address,
    SyntheticsRouter: zeroAddress,
    Timelock: zeroAddress,

    GlvReader: zeroAddress,
    GlvRouter: zeroAddress,
    GlvVault: zeroAddress,

    GelatoRelayRouter: zeroAddress,
    SubaccountGelatoRelayRouter: zeroAddress,

    OpenOceanRouter: zeroAddress,

    // GMX v2 ExternalHandler (used in multicall tests)
    ExternalHandler: "0x389cef541397e872dc04421f166b5bc2e0b374a5",

    ChainlinkPriceFeedProvider: zeroAddress,

    Multicall: rwaAddresses.MulticallHelper as Address,
    RoleStore: rwaAddresses.RoleStore as Address,
    ArbitrumNodeInterface: zeroAddress,
  },
  [BOTANIX]: {
    DataStore: "0xA23B81a89Ab9D7D89fF8fc1b5d8508fB75Cc094d",
    EventEmitter: "0xAf2E131d483cedE068e21a9228aD91E623a989C2",
    SubaccountRouter: "0x31568A44593297788Cae4D0A70b0746c26886208",
    ExchangeRouter: "0xB11214E34796d6Df5F17172D82B5F3221E17253d",
    DepositVault: "0x4D12C3D3e750e051e87a2F3f7750fBd94767742c",
    WithdrawalVault: "0x46BAeAEdbF90Ce46310173A04942e2B3B781Bf0e",
    OrderVault: "0xe52B3700D17B45dE9de7205DEe4685B4B9EC612D",
    ShiftVault: "0xa7EE2737249e0099906cB079BCEe85f0bbd837d4",

    SyntheticsReader: "0xcA3D8Ea2aCfd46D7D3732F4264bD62996A04Bb3F",
    SyntheticsRouter: "0x3d472afcd66F954Fe4909EEcDd5c940e9a99290c",

    GlvReader: "0x5AE7478d10C7298E06f38E90cf544dAE28fFE88B",
    GlvRouter: "0x8C142F1826a6679Abcc9bAa54ddbf11CC080C106",
    GlvVault: "0xd336087512BeF8Df32AF605b492f452Fd6436CD8",

    GelatoRelayRouter: "0xfF95979396B138E7e014E91932A12D78d569f3B8",
    SubaccountGelatoRelayRouter: "0x0817645a12215EAb65379AEe23fD9f9b69BAa063",

    ExternalHandler: "0x36b906eA6AE7c74aeEE8cDE66D01B3f1f8843872",
    OpenOceanRouter: zeroAddress,

    ChainlinkPriceFeedProvider: "0x8c0dF501394C0fee105f92F5CA59D7B876393B99",

    Multicall: "0x4BaA24f93a657f0c1b4A0Ffc72B91011E35cA46b",

    Timelock: "0xca3e30b51A7c3bd40bFc52a61AB0cE57B3Ab3ad8",

    ArbitrumNodeInterface: zeroAddress,

    Vault: zeroAddress,
    Reader: zeroAddress,
    PositionRouter: zeroAddress,
    ReferralStorage: zeroAddress,
    ReferralReader: zeroAddress,
    VaultReader: zeroAddress,
    GlpManager: zeroAddress,
    RewardRouter: zeroAddress,
    RewardReader: zeroAddress,
    GlpRewardRouter: zeroAddress,
    StakedGmxTracker: zeroAddress,
    FeeGmxTracker: zeroAddress,
    GLP: zeroAddress,
    GMX: zeroAddress,
    ES_GMX: zeroAddress,
    BN_GMX: zeroAddress,
    USDG: zeroAddress,
    BonusGmxTracker: zeroAddress,
    StakedGlpTracker: zeroAddress,
    FeeGlpTracker: zeroAddress,
    ExtendedGmxTracker: zeroAddress,
    StakedGmxDistributor: zeroAddress,
    StakedGlpDistributor: zeroAddress,
    GmxVester: zeroAddress,
    GlpVester: zeroAddress,
    AffiliateVester: zeroAddress,
    Router: zeroAddress,
    GovToken: zeroAddress,
    ES_GMX_IOU: zeroAddress,
    OrderBook: zeroAddress,
    OrderExecutor: zeroAddress,
    OrderBookReader: zeroAddress,
    PositionManager: zeroAddress,
    UniswapGmxEthPool: zeroAddress,

    // botanix specific
    NATIVE_TOKEN: "0x0D2437F93Fed6EA64Ef01cCde385FB1263910C56",
    StBTC: "0xF4586028FFdA7Eca636864F80f8a3f2589E33795",
    PBTC: "0x0D2437F93Fed6EA64Ef01cCde385FB1263910C56",
  },
  [ASSETCHAIN_TESTNET]: {
    Vault: zeroAddress,
    // nectar-smartcontracts/deployments/rwaTestnet/Router.json
    Router: getEnv("VITE_ASSETCHAIN_TESTNET_ROUTER_ADDRESS", "0x9d52312451dccbC75A9089864C33174f10326ac9") as Address,
    VaultReader: zeroAddress,
    Reader: rwaTestnetAddresses.Reader as Address,
    GlpManager: zeroAddress,
    RewardRouter: zeroAddress,
    RewardReader: zeroAddress,
    GlpRewardRouter: zeroAddress,
    NATIVE_TOKEN: zeroAddress,
    GLP: zeroAddress,
    GMX: zeroAddress,
    ES_GMX: zeroAddress,
    BN_GMX: zeroAddress,
    USDG: zeroAddress,
    ES_GMX_IOU: zeroAddress,

    StakedGmxTracker: zeroAddress,
    BonusGmxTracker: zeroAddress,
    FeeGmxTracker: zeroAddress,
    StakedGlpTracker: zeroAddress,
    FeeGlpTracker: zeroAddress,
    ExtendedGmxTracker: zeroAddress,

    StakedGmxDistributor: zeroAddress,
    StakedGlpDistributor: zeroAddress,

    GmxVester: zeroAddress,
    GlpVester: zeroAddress,
    AffiliateVester: zeroAddress,

    OrderBook: zeroAddress,
    OrderExecutor: zeroAddress,
    OrderBookReader: zeroAddress,

    PositionRouter: zeroAddress,
    PositionManager: zeroAddress,

    TraderJoeGmxAvaxPool: zeroAddress,
    ReferralStorage: rwaTestnetAddresses.ReferralStorage as Address,
    ReferralReader: zeroAddress,

    // Synthetics
    DataStore: getEnv("VITE_ASSETCHAIN_TESTNET_DATASTORE_ADDRESS", rwaTestnetAddresses.DataStore) as Address,
    // nectar-smartcontracts/deployments/rwaTestnet/EventEmitter.json
    EventEmitter: getEnv("VITE_ASSETCHAIN_TESTNET_EVENT_EMITTER_ADDRESS", "0x4350C696ed4F86e6431578A29735F938a88C1ca7") as Address,
    // nectar-smartcontracts/deployments/rwaTestnet/ExchangeRouter.json
    ExchangeRouter: getEnv("VITE_ASSETCHAIN_TESTNET_EXCHANGE_ROUTER_ADDRESS", "0x528ec668420A4c08a9F05560eC25e7c716e66966") as Address,
    // Not deployed on AssetChain Testnet (rwaTestnet) yet.
    SubaccountRouter: zeroAddress,
    // nectar-smartcontracts/deployments/rwaTestnet/DepositVault.json
    DepositVault: getEnv("VITE_ASSETCHAIN_TESTNET_DEPOSIT_VAULT_ADDRESS", "0x425Ab411e91a2d1D25E3A203F99BD6cd1d086221") as Address,
    // nectar-smartcontracts/deployments/rwaTestnet/WithdrawalVault.json
    WithdrawalVault: getEnv("VITE_ASSETCHAIN_TESTNET_WITHDRAWAL_VAULT_ADDRESS", "0x99e04b9D2fA565a8db9b54a1D833072879a4B0B4") as Address,
    // nectar-smartcontracts/deployments/rwaTestnet/OrderVault.json
    OrderVault: getEnv("VITE_ASSETCHAIN_TESTNET_ORDER_VAULT_ADDRESS", "0x1989aB81e0B674e78babF7b1ccc99912e8F46a39") as Address,
    // nectar-smartcontracts/deployments/rwaTestnet/ShiftVault.json
    ShiftVault: getEnv("VITE_ASSETCHAIN_TESTNET_SHIFT_VAULT_ADDRESS", "0xaf15cAED6C592Fb4C5f207ED3259c8CF7631b52C") as Address,
    // contracts repo deployments/<network>/Reader.json
    SyntheticsReader: getEnv("VITE_ASSETCHAIN_TESTNET_SYNTHETICS_READER_ADDRESS", rwaTestnetAddresses.Reader) as Address,
    // nectar-smartcontracts/deployments/rwaTestnet/Router.json
    SyntheticsRouter: getEnv("VITE_ASSETCHAIN_TESTNET_SYNTHETICS_ROUTER_ADDRESS", "0x9d52312451dccbC75A9089864C33174f10326ac9") as Address,
    Timelock: zeroAddress,

    GlvReader: zeroAddress,
    GlvRouter: zeroAddress,
    GlvVault: zeroAddress,

    GelatoRelayRouter: zeroAddress,
    SubaccountGelatoRelayRouter: zeroAddress,

    OpenOceanRouter: zeroAddress,

    // nectar-smartcontracts/deployments/rwaTestnet/ExternalHandler.json
    ExternalHandler: getEnv("VITE_ASSETCHAIN_TESTNET_EXTERNAL_HANDLER_ADDRESS", "0x74BD92FDCC67cCcbEB2D83ec79059b74c11E1653") as Address,

    // nectar-smartcontracts/deployments/rwaTestnet/ChainlinkPriceFeedProvider.json
    ChainlinkPriceFeedProvider: getEnv("VITE_ASSETCHAIN_TESTNET_CHAINLINK_PRICE_FEED_PROVIDER_ADDRESS", "0xDe275bAc95d41E2CDCA96493Da08372be0B8D523") as Address,

    Multicall: getEnv("VITE_ASSETCHAIN_TESTNET_MULTICALL_ADDRESS", rwaTestnetAddresses.Multicall3) as Address,
    ArbitrumNodeInterface: zeroAddress,
  },
  [ASSETCHAIN]: {
    Vault: zeroAddress,
    Router: zeroAddress,
    VaultReader: zeroAddress,
    Reader: zeroAddress,
    GlpManager: zeroAddress,
    RewardRouter: zeroAddress,
    RewardReader: zeroAddress,
    GlpRewardRouter: zeroAddress,
    NATIVE_TOKEN: zeroAddress,
    GLP: zeroAddress,
    GMX: zeroAddress,
    ES_GMX: zeroAddress,
    BN_GMX: zeroAddress,
    USDG: zeroAddress,
    ES_GMX_IOU: zeroAddress,

    StakedGmxTracker: zeroAddress,
    BonusGmxTracker: zeroAddress,
    FeeGmxTracker: zeroAddress,
    StakedGlpTracker: zeroAddress,
    FeeGlpTracker: zeroAddress,
    ExtendedGmxTracker: zeroAddress,

    StakedGmxDistributor: zeroAddress,
    StakedGlpDistributor: zeroAddress,

    GmxVester: zeroAddress,
    GlpVester: zeroAddress,
    AffiliateVester: zeroAddress,

    OrderBook: zeroAddress,
    OrderExecutor: zeroAddress,
    OrderBookReader: zeroAddress,

    PositionRouter: zeroAddress,
    PositionManager: zeroAddress,

    TraderJoeGmxAvaxPool: zeroAddress,
    ReferralStorage: "0x04b6002A58f5295641602240D6890a959a348f51",
    ReferralReader: zeroAddress,

    // Synthetics
    DataStore: "0x4f59597eA9287B5C13fE1cD00c0ba080C324859a",
    EventEmitter: zeroAddress,
    ExchangeRouter: zeroAddress,
    SubaccountRouter: zeroAddress,
    DepositVault: zeroAddress,
    WithdrawalVault: zeroAddress,
    OrderVault: zeroAddress,
    ShiftVault: zeroAddress,
    // contracts repo deployments/<network>/Reader.json
    SyntheticsReader: rwaAddresses.Reader as Address,
    SyntheticsRouter: zeroAddress,
    Timelock: zeroAddress,

    GlvReader: zeroAddress,
    GlvRouter: zeroAddress,
    GlvVault: zeroAddress,

    GelatoRelayRouter: zeroAddress,
    SubaccountGelatoRelayRouter: zeroAddress,

    OpenOceanRouter: zeroAddress,

    ExternalHandler: zeroAddress,

    ChainlinkPriceFeedProvider: zeroAddress,

    Multicall: "0x054C0aa7351f134Ca80794A30577a88C28629e54",
    RoleStore: "0xd68Aef575Df5c76DE49b53a074DD7f6aa8Dd355B",
    ArbitrumNodeInterface: zeroAddress,
  },
};

export function getContract(chainId: number, name: string): Address {
  const resolvedChainId = CONTRACTS[chainId] ? chainId : ASSETCHAIN;

  if (!CONTRACTS[resolvedChainId]) {
    throw new Error(`Unknown chainId ${chainId}`);
  }

  if (!CONTRACTS[resolvedChainId][name]) {
    throw new Error(`Unknown contract "${name}" for chainId ${chainId}`);
  }

  return CONTRACTS[resolvedChainId][name];
}
